import {
  AiEngineRoutingOverview,
  compareEngineVersion,
  effectiveDefault,
  isDisabled
} from "@domain/ai_engine"
import useTranslate from "@hooks/use_translate"
import BlockIcon from "@mui/icons-material/Block"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"

import { ReasonDialogRequest } from "./ReasonDialog"

type CapabilitySectionProps = {
  overview: AiEngineRoutingOverview
  ask: (request: ReasonDialogRequest) => void
  actions: {
    setDefault: (
      capability: string,
      version: string,
      reason: string
    ) => Promise<void>
    disable: (
      capability: string,
      version: string,
      reason: string
    ) => Promise<void>
    enable: (
      capability: string,
      version: string,
      reason: string
    ) => Promise<void>
  }
}

/**
 * One card per capability: which version answers by default and which versions are switched off. Every control opens the reason dialog.
 */
export default function CapabilitySection({
  overview,
  ask,
  actions
}: CapabilitySectionProps) {
  const { translate } = useTranslate()
  const versions = overview.engines
    .map((engine) => engine.version)
    .sort(compareEngineVersion)

  return (
    <Stack gap={2}>
      {overview.capabilities.map((capability) => (
        <CapabilityCard
          key={capability}
          capability={capability}
          versions={versions}
          overview={overview}
          ask={ask}
          actions={actions}
          label={translate(`ai_engine_capability_${capability}`)}
          description={translate(`ai_engine_capability_${capability}_desc`)}
        />
      ))}
    </Stack>
  )
}

function CapabilityCard({
  capability,
  versions,
  overview,
  ask,
  actions,
  label,
  description
}: CapabilitySectionProps & {
  capability: string
  versions: string[]
  label: string
  description: string
}) {
  const { translate } = useTranslate()
  const { routing } = overview
  const current = effectiveDefault(routing, capability)

  const changeDefault = (version: string) => {
    if (version === current) {
      return
    }
    ask({
      title: translate("ai_engine_set_default_title"),
      summary: translate("ai_engine_set_default_summary")
        .replace("{capability}", label)
        .replace("{from}", current)
        .replace("{to}", version),
      danger: true,
      onConfirm: (reason) => actions.setDefault(capability, version, reason)
    })
  }

  const toggleDisabled = (version: string) => {
    const disabled = isDisabled(routing, capability, version)
    ask({
      title: translate(
        disabled ? "ai_engine_enable_title" : "ai_engine_disable_title"
      ),
      summary: translate(
        disabled ? "ai_engine_enable_summary" : "ai_engine_disable_summary"
      )
        .replace("{capability}", label)
        .replace("{version}", version),
      danger: !disabled,
      onConfirm: (reason) =>
        disabled
          ? actions.enable(capability, version, reason)
          : actions.disable(capability, version, reason)
    })
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack gap={2}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>

        <Stack gap={1}>
          <Typography variant="subtitle2">
            {translate("ai_engine_default_title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate("ai_engine_default_desc")}
          </Typography>
          <FormControl size="small" sx={{ maxWidth: 240 }}>
            <InputLabel>{translate("ai_engine_version")}</InputLabel>
            <Select
              label={translate("ai_engine_version")}
              value={current}
              onChange={(event) => changeDefault(String(event.target.value))}
            >
              {versions.map((version) => (
                <MenuItem key={version} value={version}>
                  {version}
                  {isDisabled(routing, capability, version)
                    ? ` (${translate("ai_engine_disabled_short")})`
                    : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Divider />

        <Stack gap={1}>
          <Typography variant="subtitle2">
            {translate("ai_engine_switch_title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate("ai_engine_switch_desc")}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {versions.map((version) => {
              const disabled = isDisabled(routing, capability, version)
              return (
                <Tooltip
                  key={version}
                  title={
                    disabled
                      ? translate("ai_engine_switch_on_hint")
                      : translate("ai_engine_switch_off_hint")
                  }
                >
                  <Button
                    size="small"
                    variant={disabled ? "contained" : "outlined"}
                    color={disabled ? "error" : "inherit"}
                    startIcon={
                      disabled ? <BlockIcon /> : <CheckCircleOutlineIcon />
                    }
                    onClick={() => toggleDisabled(version)}
                  >
                    {version}:{" "}
                    {disabled
                      ? translate("ai_engine_disabled_short")
                      : translate("ai_engine_enabled_short")}
                  </Button>
                </Tooltip>
              )
            })}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  )
}
